import { useRef, useMemo, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { DemonState } from './types'
import type { DemonData } from './types'
import { buildDemonParticles } from './demonShape'
import { getDemonNoiseTexture } from './noiseTexture'
import {
  demonBodyVertexShader,
  demonBodyFragmentShader,
  demonGlowVertexShader,
  demonGlowFragmentShader,
} from './demonShaders'

interface DemonEntityProps {
  /** Mutable reference — read directly in useFrame, not via React props */
  data: DemonData
}

// Temp vectors (module-level, reused across frames)
const _up = new THREE.Vector3()
const _east = new THREE.Vector3()
const _north = new THREE.Vector3()
const _fwd = new THREE.Vector3()
const _right = new THREE.Vector3()
const _rotMat = new THREE.Matrix4()

/** Scale factor: demon is ~6 local units tall, we want ~12 world units tall */
const LOCAL_SCALE = 2.0

function getHealthColor(pct: number): string {
  if (pct > 60) return '#ff2200'
  if (pct > 30) return '#ff6600'
  return '#ff0000'
}

export function DemonEntity({ data }: DemonEntityProps) {
  const bodyRef = useRef<THREE.Points>(null)
  const glowRef = useRef<THREE.Points>(null)
  const groupRef = useRef<THREE.Group>(null)
  const prevState = useRef(data.state)
  const stateProgress = useRef(0)

  // Health bar visibility — update at low frequency to avoid React churn
  const [healthPct, setHealthPct] = useState(100)
  const [visible, setVisible] = useState(true)
  const healthCheckTimer = useRef(0)

  // Build particle buffers once
  const buffers = useMemo(() => buildDemonParticles(), [])

  // Shared baked noise texture (singleton)
  const noiseTex = useMemo(() => getDemonNoiseTexture(), [])

  const bodyUniforms = useMemo(() => ({
    uTime: { value: 0 },
    uState: { value: 0 },
    uStateProgress: { value: 0 },
    uNoiseTex: { value: noiseTex },
  }), [noiseTex])

  const glowUniforms = useMemo(() => ({
    uTime: { value: 0 },
    uState: { value: 0 },
    uStateProgress: { value: 0 },
    uNoiseTex: { value: noiseTex },
  }), [noiseTex])

  // Build buffer geometry once
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(buffers.positions, 3))
    geo.setAttribute('aOffset', new THREE.BufferAttribute(buffers.offsets, 3))
    geo.setAttribute('aVelocity', new THREE.BufferAttribute(buffers.velocities, 3))
    geo.setAttribute('aRole', new THREE.BufferAttribute(buffers.roles, 1))
    geo.setAttribute('aRandom', new THREE.BufferAttribute(buffers.randoms, 1))
    geo.setAttribute('aSize', new THREE.BufferAttribute(buffers.sizes, 1))
    return geo
  }, [buffers])

  useFrame(({ clock }, delta) => {
    if (!groupRef.current) return
    const t = clock.elapsedTime

    const { nx, ny, nz, radius, heading, state, health } = data

    // ── Hide dead demons after death animation completes ──
    if (state === DemonState.Death && stateProgress.current >= 1) {
      if (visible) setVisible(false)
      return
    }

    // ── Update group transform ──
    const footOffset = 1.5 * LOCAL_SCALE
    const worldR = radius + footOffset
    groupRef.current.position.set(nx * worldR, ny * worldR, nz * worldR)

    _up.set(nx, ny, nz)
    if (Math.abs(ny) < 0.9) {
      _east.set(-nz, 0, nx)
    } else {
      _east.set(0, nz, -ny)
    }
    _east.normalize()
    _north.crossVectors(_up, _east).normalize()

    const cosH = Math.cos(heading)
    const sinH = Math.sin(heading)
    _fwd.set(
      _east.x * sinH + _north.x * cosH,
      _east.y * sinH + _north.y * cosH,
      _east.z * sinH + _north.z * cosH,
    )
    _right.crossVectors(_up, _fwd)
    _rotMat.makeBasis(_right, _up, _fwd)
    groupRef.current.quaternion.setFromRotationMatrix(_rotMat)
    groupRef.current.scale.setScalar(LOCAL_SCALE)

    // ── State transition progress ──
    if (prevState.current !== state) {
      prevState.current = state
      stateProgress.current = 0
    }
    stateProgress.current = Math.min(1, stateProgress.current + delta * (state === DemonState.Death ? 0.5 : 0.5))

    // ── Update uniforms ──
    bodyUniforms.uTime.value = t
    bodyUniforms.uState.value = state
    bodyUniforms.uStateProgress.value = stateProgress.current

    glowUniforms.uTime.value = t
    glowUniforms.uState.value = state
    glowUniforms.uStateProgress.value = stateProgress.current

    // ── Update health bar at low frequency ──
    healthCheckTimer.current += delta
    if (healthCheckTimer.current > 0.2) {
      healthCheckTimer.current = 0
      const pct = Math.round(health)
      if (pct !== healthPct) setHealthPct(pct)
    }
  })

  if (!visible) return null

  return (
    <group ref={groupRef}>
      {/* Pass 1: Dark body */}
      <points ref={bodyRef} geometry={geometry} renderOrder={1} frustumCulled={false}>
        <shaderMaterial
          uniforms={bodyUniforms}
          vertexShader={demonBodyVertexShader}
          fragmentShader={demonBodyFragmentShader}
          transparent
          depthWrite={false}
          depthTest
        />
      </points>

      {/* Pass 2: Glow embers + eyes */}
      <points ref={glowRef} geometry={geometry} renderOrder={2} frustumCulled={false}>
        <shaderMaterial
          uniforms={glowUniforms}
          vertexShader={demonGlowVertexShader}
          fragmentShader={demonGlowFragmentShader}
          transparent
          depthWrite={false}
          depthTest
          blending={THREE.AdditiveBlending}
        />
      </points>

      {/* Health bar — only show when damaged */}
      {healthPct < 100 && (
        <group position={[0, 4.5, 0]}>
          <Html center distanceFactor={50} style={{ pointerEvents: 'none' }}>
            <div style={{
              width: 60,
              height: 6,
              background: '#1a1a1a',
              borderRadius: 3,
              border: '1px solid #333',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${healthPct}%`,
                height: '100%',
                background: getHealthColor(healthPct),
                transition: 'width 0.15s ease-out',
                borderRadius: 3,
                boxShadow: `0 0 4px ${getHealthColor(healthPct)}`,
              }} />
            </div>
          </Html>
        </group>
      )}
    </group>
  )
}
